import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
    MdAddCircle as PlusCircle,
    MdEdit as Pencil,
    MdDelete as Trash2,
    MdClose as X,
    MdWarning as AlertTriangle,
    MdPalette as Palette,
    MdClear as Clear,
  } from 'react-icons/md';
import Modal from 'react-modal';
import Menu from "@components/Menu";
import EmpresaSelect from "@components/EmpresaSelect";
import TestEnvironmentAlert from "@components/TestEnvironmentAlert";
import { HexColorPicker } from 'react-colorful';
import UserContext from "@components/context/userContext";
import segmentacaoService from "@services/segmentacao";

Modal.setAppElement('#root');

const defaultSegmentacao = {
  idsegmentacao: 0,
  nome: '',
  color: '#000000',
};

const PREDEFINED_COLORS = [
  { name: 'Vermelho', hex: '#FF0000' },
  { name: 'Verde', hex: '#00FF00' },
  { name: 'Azul', hex: '#0000FF' },
  { name: 'Amarelo', hex: '#FFD700' },
  { name: 'Laranja', hex: '#FFA500' },
  { name: 'Roxo', hex: '#800080' },
  { name: 'Rosa', hex: '#FF69B4' },
  { name: 'Castanho', hex: '#8B4513' },
  { name: 'Cinzento', hex: '#808080' },
  { name: 'Preto', hex: '#000000' },
  { name: 'Branco', hex: '#FFFFFF' },
  { name: 'Turquesa', hex: '#40E0D0' }
];

const SegmentacaoForm = React.memo(({ segmentacao, onChange, isNew = false }) => (
  <div className="form-horizontal">
    {!isNew && (
      <div className="form-group">
        <label className="col-sm-3 control-label">ID da Segmentação</label>
        <div className="col-sm-9">
          <input
            type="number"
            className="form-control"
            value={segmentacao.idsegmentacao}
            disabled
            style={{ backgroundColor: '#f5f5f5' }}
          />
        </div>
      </div>
    )}
    
    <div className="form-group">
      <label className="col-sm-3 control-label">Nome</label>
      <div className="col-sm-9">
        <input
          type="text"
          className="form-control"
          value={segmentacao.nome}
          onChange={(e) => onChange('nome', e.target.value)}
          placeholder="Nome da segmentação"
        />
      </div>
    </div>
    
    {!isNew && (
    <ColorPickerSection
      color={segmentacao.color}
      onChange={(color) => onChange('color', color)}
    />)}

  </div>
));

const ColorPickerSection = React.memo(({ color, onChange }) => {
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  return (
    <div className="form-group">
      <label className="col-sm-3 control-label">Cor</label>
      <div className="col-sm-9">
        <div className="color-picker-grid">
          {PREDEFINED_COLORS.map((presetColor) => (
            <button
              key={presetColor.hex}
              onClick={() => {
                onChange(presetColor.hex);
                setShowCustomPicker(false);
              }}
              className={`color-swatch ${color === presetColor.hex ? 'active' : ''}`}
              style={{ backgroundColor: presetColor.hex }}
              title={presetColor.name}
            />
          ))}
        </div>
        
        <button
          onClick={() => setShowCustomPicker(!showCustomPicker)}
          className="btn btn-secondary"
        >
          <Palette size={20} className="btn-icon" />
          {showCustomPicker ? 'Esconder Seletor de Cor' : 'Cor Personalizada'}
        </button>
        
        {showCustomPicker && (
          <div className="custom-color-picker">
            <HexColorPicker color={color} onChange={onChange} />
            <div className="input-group" style={{ marginTop: '10px', width: '200px' }}>
              <span className="input-group-addon">
                <div 
                  style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: color,
                    border: '1px solid #ddd',
                    borderRadius: '2px'
                  }}
                />
              </span>
              <input
                type="text"
                value={color}
                onChange={(e) => onChange(e.target.value)}
                className="form-control"
                pattern="^#[0-9A-Fa-f]{6}$"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

function App() {
    const profile = useContext(UserContext);
    
  const [segmentacoes, setSegmentacoes] = useState([]);
  const [editingSegmentacao, setEditingSegmentacao] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [segmentacaoToDelete, setSegmentacaoToDelete] = useState(null);
  const [newSegmentacao, setNewSegmentacao] = useState({ ...defaultSegmentacao });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);

  const fetchSegmentacoes = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await segmentacaoService.get(profile.idEmpresaSec);
      if (Array.isArray(data)) {
        const formattedSegmentacoes = data.map((item) => ({
          idsegmentacao: parseInt(item.idsegmentacao),
          nome: item.nome,
          color: item.color || '#000000',
          idempresa: profile.idEmpresaSec
        }));
        setSegmentacoes(formattedSegmentacoes.sort((a, b) => a.idsegmentacao - b.idsegmentacao));
      } else {
        setSegmentacoes([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar segmentações');
      setSegmentacoes([]);
    } finally {
      setIsLoading(false);
    }
  }, [profile.idEmpresaSec]);

  useEffect(() => {
    import('./list.css').then(() => {});
    fetchSegmentacoes();
  }, [fetchSegmentacoes]);

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleCreate = async () => {
    try {
      if (!newSegmentacao.nome.trim()) {
        setError('O nome da segmentação é obrigatório');
        return;
      }

      const responseData = await segmentacaoService.set({
        idempresa: profile.idEmpresaSec,
        nome: newSegmentacao.nome,
      });
      
      if (responseData === 'NOK') {
        throw new Error('Erro ao criar segmentação');
      }

      setNewSegmentacao({ ...defaultSegmentacao });
      setIsModalOpen(false);
      setError(null);
      await fetchSegmentacoes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar segmentação');
    }
  };

  const handleEdit = (segmentacao) => {
    setEditingSegmentacao(segmentacao);
    setIsEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingSegmentacao) return;

    try {
      const responseData = await segmentacaoService.set({
        idempresa: profile.idEmpresaSec,
        idsegmentacao: editingSegmentacao.idsegmentacao.toString(),
        nome: editingSegmentacao.nome,
        color: editingSegmentacao.color
      });
      
      if (responseData.status === 'NOK') {
        throw new Error(responseData.message || 'Erro ao atualizar segmentação');
      }

      setSegmentacoes(segmentacoes.map(c => 
        c.idsegmentacao === editingSegmentacao.idsegmentacao ? editingSegmentacao : c
      ));
      setIsEditModalOpen(false);
      setEditingSegmentacao(null);
      setError(null);
      
      await fetchSegmentacoes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar segmentação');
    }
  };

  const handleDeleteModal = (segmentacaoId) => {
    setSegmentacaoToDelete(segmentacaoId);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!segmentacaoToDelete) return;

    try {
      const data = await segmentacaoService.unset(profile.idEmpresaSec, segmentacaoToDelete);
      
      if (data.status === 'OK') {
        setSegmentacoes(segmentacoes.filter(c => c.idsegmentacao !== segmentacaoToDelete));
        setDeleteModalOpen(false);
        setSegmentacaoToDelete(null);
        setError(null);
      } else {
        throw new Error(data.message || 'Erro ao eliminar segmentação');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao eliminar segmentação');
    }
  };

  const getActiveTags = (segmentacao) => {
    const tags = [];
    return tags;
  };

  const filteredSegmentacoes = segmentacoes.filter(segmentacao => {
    const searchLower = searchQuery.toLowerCase();
    const tags = getActiveTags(segmentacao);
    
    return (
      segmentacao.idsegmentacao.toString().includes(searchQuery) ||
      segmentacao.nome.toLowerCase().includes(searchLower) ||
      tags.some(tag => tag.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div>
        <Menu />
        <div className="fullContainer" style={{ maxWidth: '100%' }}>
            <div className="col-xs-12">
                <ol
                style={{
                    textAlign: 'left',
                    marginTop: '75px',
                }}
                className="breadcrumb"
                >
                <li>
                    <a href="http://eposgestofinal.com/index.php">Portal</a>
                </li>
                <li className="active">Gestão de Segmentações</li>
                </ol>
            </div>
        </div>
        <EmpresaSelect />
        <div className="fullContainer" style={{ padding: '20px', maxWidth: '100%' }}>
        <div className="page-header">
            <div className="row">
            <div className="col-sm-6">
                <h1>Segmentações</h1>
            </div>
            <div className="col-sm-6 text-right">
                <button
                onClick={() => setIsModalOpen(true)}
                className="btn btn-primary btn-lg"
                >
                <PlusCircle size={24} className="btn-icon" />
                Nova Segmentação
                </button>
            </div>
            </div>
        </div>

        {!isLoading && (
          <div className="stats-container">
            <div className="stat-card">
              <div className="stat-value">{segmentacoes.length}</div>
              <div className="stat-label">Total de Segmentações</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{filteredSegmentacoes.length}</div>
              <div className="stat-label">Resultados</div>
            </div>
          </div>
        )}

        <div className="search-wrapper">
          <div className="search-container">
            <input
              ref={searchInputRef}
              type="text"
              className="form-control search-input"
              placeholder="Pesquisar por nome, ID ou tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="search-clear-btn"
                onClick={() => setSearchQuery('')}
                title="Limpar pesquisa"
              >
                <Clear size={20} />
              </button>
            )}
            <div className="search-shortcut">
              <kbd>⌘K</kbd>
            </div>
          </div>
          {searchQuery && (
            <div className="search-results-info">
              <span className="results-badge">
                {filteredSegmentacoes.length} {filteredSegmentacoes.length === 1 ? 'resultado' : 'resultados'}
              </span>
              {filteredSegmentacoes.length === 0 && (
                <span className="no-match-text">Nenhuma correspondência encontrada</span>
              )}
            </div>
          )}
        </div>

        {error && (
            <div className="alert alert-danger">
            <AlertTriangle size={24} className="btn-icon" />
            {error}
            </div>
        )}

        {isLoading ? (
            <div className="loading">
            <div style={{ 
                width: '48px', 
                height: '48px', 
                border: '4px solid #e5e7eb', 
                borderTopColor: '#4f46e5',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
            }} />
            <p>Carregando segmentações...</p>
            </div>
        ) : (
            <div>
            {filteredSegmentacoes.length === 0 ? (
                <div className="no-results">
                <p>Nenhuma segmentação encontrada</p>
                </div>
            ) : (
                filteredSegmentacoes.map((segmentacao) => (
                <div key={segmentacao.idsegmentacao} className="segmentacao-card">
                    <div className="category-header">
                    <div
                        className="color-circle"
                        style={{ backgroundColor: segmentacao.color }}
                    />
                    <span className="segmentacao-id">#{segmentacao.idsegmentacao}</span>
                    <h3 className="segmentacao-name">{segmentacao.nome}</h3>
                    <div className="pull-right">
                        <button
                        onClick={() => handleEdit(segmentacao)}
                        className="btn btn-link"
                        >
                        <Pencil size={24} className="text-primary" />
                        </button>
                        <button
                        onClick={() => handleDeleteModal(segmentacao.idsegmentacao)}
                        className="btn btn-link"
                        >
                        <Trash2 size={24} className="text-danger" />
                        </button>
                    </div>
                    </div>
                    <div>
                    </div>
                </div>
                ))
            )}
            </div>
        )}

        <Modal
            isOpen={isModalOpen}
            onRequestClose={() => {
            setIsModalOpen(false);
            setNewSegmentacao({ ...defaultSegmentacao });
            setError(null);
            }}
            className="modal-content"
            overlayClassName="modal-overlay"
            contentLabel="Nova Categoria"
        >
            <div className="modal-header">
            <h2 className="modal-title">Nova Segmentação</h2>
            <button
                onClick={() => {
                setIsModalOpen(false);
                setNewSegmentacao({ ...defaultSegmentacao });
                setError(null);
                }}
                className="close"
            >
                <X size={24} />
            </button>
            </div>
            <div className="modal-body">
            <SegmentacaoForm
                segmentacao={newSegmentacao}
                onChange={(field, value) => setNewSegmentacao({ ...newSegmentacao, [field]: value })}
                isNew={true}
            />
            </div>
            <div className="modal-footer">
            <button
                onClick={() => {
                setIsModalOpen(false);
                setNewSegmentacao({ ...defaultSegmentacao });
                setError(null);
                }}
                className="btn btn-primary btn-lg"
            >
                Cancelar
            </button>
            <button
                onClick={handleCreate}
                className="btn btn-success btn-lg"
            >
                Criar
            </button>
            </div>
        </Modal>

        <Modal
            isOpen={isEditModalOpen}
            onRequestClose={() => {
            setIsEditModalOpen(false);
            setEditingSegmentacao(null);
            }}
            className="modal-content"
            overlayClassName="modal-overlay"
            contentLabel="Editar Categoria"
        >
            {editingSegmentacao && (
            <>
                <div className="modal-header">
                <h2 className="modal-title">Editar Categoria</h2>
                <button
                    onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingSegmentacao(null);
                    }}
                    className="close"
                >
                    <X size={24} />
                </button>
                </div>
                <div className="modal-body">
                <SegmentacaoForm
                    segmentacao={editingSegmentacao}
                    onChange={(field, value) => setEditingSegmentacao({ ...editingSegmentacao, [field]: value })}
                />
                </div>
                <div className="modal-footer">
                <button
                    onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingSegmentacao(null);
                    }}
                    className="btn btn-primary btn-lg"
                >
                    Cancelar
                </button>
                <button
                    onClick={handleUpdate}
                    className="btn btn-success btn-lg"
                >
                    Salvar
                </button>
                </div>
            </>
            )}
        </Modal>

        <Modal
            isOpen={deleteModalOpen}
            onRequestClose={() => {
            setDeleteModalOpen(false);
            setSegmentacaoToDelete(null);
            }}
            className="modal-content"
            overlayClassName="modal-overlay"
            contentLabel="Confirmar Eliminação"
        >
            <div className="modal-header">
            <h2 className="modal-title">Confirmar Eliminação</h2>
            <button
                onClick={() => {
                setDeleteModalOpen(false);
                setSegmentacaoToDelete(null);
                }}
                className="close"
            >
                <X size={24} />
            </button>
            </div>
            <div className="modal-body">
            <p>Tem certeza que deseja eliminar esta segmentação?</p>
            </div>
            <div className="modal-footer">
            <button
                onClick={() => {
                setDeleteModalOpen(false);
                setSegmentacaoToDelete(null);
                }}
                className="btn btn-primary btn-lg"
            >
                Cancelar
            </button>
            <button
                onClick={handleDelete}
                className="btn btn-danger btn-lg"
            >
                Eliminar
            </button>
            </div>
        </Modal>
        </div>
    </div>
  );
}

export default App;